import { PropsWithChildren, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { FormikGroupContext, FormikGroupItemContextData } from './formik-group-context';

interface FormikGroupProps extends PropsWithChildren {
    activeStep: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    stepperState: any;
}

export const FormikGroup = forwardRef((props: FormikGroupProps, ref) => {
    const items = useRef<FormikGroupItemContextData[]>([]).current;
    const stepperState = useState({});

    // Whatever is on screen. A tab that holds head and positions together has
    // two forms open at once, and one "Speichern" has to reach both of them
    // (#2208). A step that is not mounted has registered nothing and is left
    // out by itself.
    const mounted = () => items.filter(Boolean);

    useImperativeHandle(ref, () => ({
        submit: async () => {
            const results = await Promise.all(mounted().map(item => item.submit()));

            return results.reduce((all, errors) => ({ ...all, ...(errors ?? {}) }), {});
        },
        validate: async () => {
            // A step without a form has nothing to say — the chain view is one
            // of those, and asking it would take the whole mask down.
            const results = await Promise.all(mounted().map(item => item.validate()));

            return results.reduce((all, errors) => ({ ...all, ...(errors ?? {}) }), {});
        },
    }));

    // Both keep their identity for the life of the group. A form drops itself
    // on unmount through an effect that depends on `unregister`; when that
    // changed on every render, the effect re-ran and took the form out of the
    // registry it had just entered. Which forms were left at the moment of a
    // save was then a matter of timing — the head of the invoice was regularly
    // missing, so it was never validated and never saved (#2210).
    const register = useCallback(
        (id: number, item: FormikGroupItemContextData) => {
            items[id] = item;
        },
        [items]
    );

    const unregister = useCallback(
        (id: number) => {
            delete items[id];
        },
        [items]
    );

    const value = useMemo(
        () => ({ test: 'test', items, register, unregister, state: stepperState }),
        [items, register, unregister, stepperState]
    );

    return <FormikGroupContext.Provider value={value}>{props.children}</FormikGroupContext.Provider>;
});
